import React from 'react';
import {useTranslation} from 'react-i18next';
import DateRangeFilter from './filters/DateRangeFilter';
import LocationFilter from './filters/LocationFilter';
import PlacesFilter from './filters/PlacesFilter';
import PeopleFilter from './filters/PeopleFilter';
import WorkshopsFilter from './filters/WorkshopsFilter';
import PublicationsFilter from './filters/PublicationsFilter';
import CollectionsFilter from './filters/CollectionsFilter';
import SortingFilter from './filters/SortingFilter';
import {SearchCriteria, SortingOptions} from '../types/SearchCriteria';

export type FilterName =
  'dateRange'
  | 'location'
  | 'places'
  | 'people'
  | 'workshops'
  | 'publications'
  | 'collections'
  | 'sorting';

interface FiltersBarProps {
  searchCriteria: SearchCriteria;
  onSearchCriteriaChange: (criteria: SearchCriteria) => void;
  disabledFilters?: FilterName[];
}

const FiltersBar: React.FC<FiltersBarProps> = ({
  searchCriteria,
                                                 onSearchCriteriaChange,
                                                 disabledFilters = []
}) => {
  const {t} = useTranslation();
  const isFilterDisabled = (filterName: FilterName): boolean => {
    return disabledFilters.includes(filterName);
  };
  const handleDateRangeChange = (yearFrom?: number, yearTo?: number) => {
    onSearchCriteriaChange({
      ...searchCriteria,
      yearFrom,
      yearTo
    });
  };

  const handleLocationChange = (organizationalUnits?: string[], hierarchyType?: string) => {
    onSearchCriteriaChange({
      ...searchCriteria,
      organizationalUnits,
      hierarchyType
    });
  };

  const handlePlacesChange = (places?: string[]) => {
    onSearchCriteriaChange({
      ...searchCriteria,
      places
    });
  };

  const handlePeopleChange = (people?: string[]) => {
    onSearchCriteriaChange({
      ...searchCriteria,
      people
    });
  };

  const handleWorkshopsChange = (workshops?: string[]) => {
    onSearchCriteriaChange({
      ...searchCriteria,
      workshops
    });
  };

  const handlePublicationsChange = (publications?: string[]) => {
    onSearchCriteriaChange({
      ...searchCriteria,
      publications
    });
  };

  const handleCollectionsChange = (collections?: string[]) => {
    onSearchCriteriaChange({
      ...searchCriteria,
      collections
    });
  };

  const handleSortingChange = (sortingOptions?: SortingOptions) => {
    onSearchCriteriaChange({
      ...searchCriteria,
      sortingOptions
    });
  };

  return (
    <section id="filters-section" className="border-b border-gray-300 bg-white">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between flex-wrap">
          <div className="flex items-center space-x-6 text-sm">
            <div className="font-medium">{t('gallery.filters.filterBy')}</div>

            {!isFilterDisabled('dateRange') && (
              <DateRangeFilter
                yearFrom={searchCriteria.yearFrom}
                yearTo={searchCriteria.yearTo}
                onFilterChange={handleDateRangeChange}
              />
            )}

            {!isFilterDisabled('location') && (
              <LocationFilter
                organizationalUnits={searchCriteria.organizationalUnits}
                hierarchyType={searchCriteria.hierarchyType}
                onFilterChange={handleLocationChange}
              />
            )}

            {!isFilterDisabled('places') && (
              <PlacesFilter
                places={searchCriteria.places}
                onFilterChange={handlePlacesChange}
              />
            )}

            {!isFilterDisabled('people') && (
              <PeopleFilter
                people={searchCriteria.people}
                onFilterChange={handlePeopleChange}
              />
            )}

            {!isFilterDisabled('workshops') && (
              <WorkshopsFilter
                workshops={searchCriteria.workshops}
                onFilterChange={handleWorkshopsChange}
              />
            )}

            {!isFilterDisabled('publications') && (
              <PublicationsFilter
                publications={searchCriteria.publications}
                onFilterChange={handlePublicationsChange}
              />
            )}

            {!isFilterDisabled('collections') && (
              <CollectionsFilter
                collections={searchCriteria.collections}
                onFilterChange={handleCollectionsChange}
              />
            )}
          </div>
          <div className="flex items-center space-x-4 text-sm">
            {!isFilterDisabled('sorting') && (
              <SortingFilter
                sortingOptions={searchCriteria.sortingOptions}
                onFilterChange={handleSortingChange}
              />
            )}
            {/*<div className="flex items-center cursor-pointer hover:text-gray-600">*/}
            {/*  <i className="mr-1 fa fa-sliders"/>*/}
            {/*  <span>Advanced</span>*/}
            {/*</div>*/}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FiltersBar;
